import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Program, ProgramUser } from '../model/program';
import { getApiAccessToken } from '../auth/auth';
import { useMsal } from '@azure/msal-react';
import { addUsersToProgram, cloneProgram, deleteProgram, getPrograms, getProgramUsers, putProgram, removeUserFromProgram } from '../api/programs';
import { log } from 'console';
import { deleteCourse } from '../api/courses';
import { Group } from '../model/group';
import { User } from '../model/user';

interface ProgramState {
  programs: Program[]
  currentProgram: Program | null
}

const initialState: ProgramState = {
  programs: [],
  currentProgram: null,
};

const programSlice = createSlice({
  name: 'program',
  initialState,
  reducers: {
    initPrograms: (state, action: PayloadAction<Program[]>) => {
      state.programs = action.payload;
    },
    addProgram: (state, action: PayloadAction<Program>) => {
      state.programs.push(action.payload);
      state.currentProgram = action.payload;
    },
    setCurrentProgram: (state, action: PayloadAction<Program>) => {
      let valid = state.programs.map(program => program.id).includes(action.payload.id)

      if (valid) {
        state.currentProgram = action.payload
      }
    },
    setFirstProgramInGroup: (state, action: PayloadAction<Group>) => {
      const clientPrograms = state.programs.filter(program => program.clientName === action.payload.displayName);
      if (clientPrograms.length != 0) {
        state.currentProgram = clientPrograms[0];
      }
    },
    updateProgram: (state, action: PayloadAction<Program>) => {
        state.programs.forEach((program, index) => {
            if (program.id == action.payload.id) {
                state.programs[index] = action.payload
            }
        })

        if (state.currentProgram?.id == action.payload.id) {
          state.currentProgram = action.payload
        }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProgramsAsync.fulfilled, (state: ProgramState, action: PayloadAction<Program[]>) => {
        state.programs = action.payload;
      })
      .addCase(getProgramsAsync.rejected, (state: ProgramState, action) => {
        console.error(action.error.message);
      })
      .addCase(addProgramAsync.fulfilled, (state: ProgramState, action: PayloadAction<Program>) => {
        state.programs.push(action.payload);
        state.currentProgram = action.payload;
      })
      .addCase(addProgramAsync.rejected, (state: ProgramState, action) => {
        console.error(action.error.message);
      })
      .addCase(cloneProgramAsync.fulfilled, (state: ProgramState, action: PayloadAction<Program>) => {
        state.programs.push(action.payload);
        state.currentProgram = action.payload;
      })
      .addCase(cloneProgramAsync.rejected, (state: ProgramState, action) => {
        console.error(action.error.message);
      })
      .addCase(deleteProgramAsync.fulfilled, (state: ProgramState, action) => {
        const removedProgramId = action.meta.arg.program.id;
        state.programs = state.programs.filter(program => program.id !== removedProgramId);
        
        let clientPrograms = state.programs.filter(program => program.clientName === state.currentProgram?.clientName)
        state.currentProgram = clientPrograms.length > 0 ? clientPrograms[0] : null;
      })
      .addCase(deleteProgramAsync.rejected, (state: ProgramState, action) => {
        console.error(action.error.message);
      })
  },
});

export const getProgramsAsync = createAsyncThunk('program/getProgramsAsync', async ({ accessToken }: { accessToken: string}) => {
    const response = await getPrograms(accessToken);
    return response;
  }
);

export const addProgramAsync = createAsyncThunk('program/addProgramAsync', async ({ program, accessToken }: { program: Program; accessToken: string}) => {
    const response = await putProgram(accessToken, program);
    return response;
  }
);

export const cloneProgramAsync = createAsyncThunk('program/cloneProgramAsync', async ({ clientName, program, accessToken }: { clientName: string, program: Program; accessToken: string}) => {
    const response = await cloneProgram(accessToken, clientName, program);
    return response;
  }
);

export const deleteProgramAsync = createAsyncThunk('program/deleteProgramAsync', async ({ accessToken, program }: { accessToken: string, program: Program}) => {

    // first delete all courses in the program
    const deletePromises = program.courses.map(course => deleteCourse(accessToken, course.uuid));

    await Promise.allSettled(deletePromises);

    // then delete the program itself
    const response = await deleteProgram(accessToken, program.id);
    return response;
  }
);

export const { initPrograms, setCurrentProgram, setFirstProgramInGroup, addProgram, updateProgram} = programSlice.actions;
export default programSlice.reducer;